import { imgElementSvg } from '../../../components/shared/vector-images';
import { getStore } from '../../store';
import { shiftLatLonByPixelRelativeToMapContainer } from '../../utilities';
import { layerExists, setDataSource, sourceExists } from '../_helpers';

let mapReference;
let currentDisplayTripPlanMarkers = [];

const addLayersIfRequired = () => {
  if (!sourceExists(mapReference, 'tripPlan')) {
    mapReference.addSource('tripPlan', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }
  if (!layerExists(mapReference, 'tripPlan')) {
    mapReference.addLayer({
      id: 'tripPlan',
      type: 'symbol',
      source: 'tripPlan',
      layout: {
        'icon-image': ['to-string', ['get', 'icon']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8.4999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0.5],
        ],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
};

const layer = {
  displayName: 'Trip Plan',
  synonyms: '',
  group: 'tileLayer',
  typeUrl: 'tripPlan',
  shownByDefault: true,
  relativeDepth: 10,
  menuOptions: {
    visible: true,
    owningMenu: 'log-trips',
    order: 0,
  },
  icon: null,
};

const loadMarkerIconAsImage = async (icon) => {
  const iconName = icon.name;
  if (!mapReference.hasImage(iconName)) {
    const marker = await imgElementSvg(icon(), {
      width: 100,
      height: 100,
    });
    mapReference.addImage(iconName, marker);
  }

  return iconName;
};

const updateLayerGeoJSONData = () => {
  let geojson = {
    type: 'FeatureCollection',
    features: [],
  };

  if (!getStore().getState()?.logTrips?.showingTripPlanPoints) {
    geojson = {
      type: 'FeatureCollection',
      features: currentDisplayTripPlanMarkers.map((marker) => marker.data),
    };
  }

  setDataSource(mapReference, 'tripPlan', geojson);
};
const formatAsGeoJSONPoint = (longitude, latitude, properties = {}) => ({
  type: 'geojson',
  data: {
    type: 'Feature',
    properties,
    geometry: {
      type: 'Point',
      coordinates: [longitude, latitude],
    },
  },
});

const calculateMapPadding = () => {
  const isMobileWidth = window.innerWidth < 800;
  const defaultPadding = isMobileWidth ? 40 : 100;

  // Initialise the dimensions
  let topHeaderHeight = 0;
  let tripDetailsPanelWidth = 0;
  let tripDetailsPanelHeight = 0;

  // get panel element
  const topHeaderElement = document.getElementById('pageHolderStickyHeader');
  const tripDetailsPanel = document.getElementById('mapPointSelector');

  // get panel dimensions
  if (topHeaderElement && tripDetailsPanel) {
    topHeaderHeight = topHeaderElement?.getBoundingClientRect()?.height || 0;
    tripDetailsPanelWidth =
      tripDetailsPanel?.getBoundingClientRect()?.width || 0;
    tripDetailsPanelHeight =
      tripDetailsPanel?.getBoundingClientRect()?.height || 0;
  }

  // get our safe area heights
  const safeAreaTop = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--sat') || 0,
    10,
  );
  const safeAreaBottom = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--sab') || 0,
    10,
  );

  // Setup the mobile layout map padding (<800px width)
  let paddingTop = 20 + safeAreaTop + topHeaderHeight;
  let paddingBottom = 20 + tripDetailsPanelHeight + safeAreaBottom;
  let paddingLeft = defaultPadding;
  let paddingRight = defaultPadding;

  // Desktop layout padding - 800px+ width
  if (!isMobileWidth) {
    paddingTop = defaultPadding + safeAreaTop + tripDetailsPanelHeight / 2;
    paddingBottom = defaultPadding + safeAreaBottom;
    paddingLeft = defaultPadding + tripDetailsPanelWidth / 2;
    paddingRight = defaultPadding;
  }

  const padding = {
    top: Math.round(paddingTop),
    bottom: Math.round(paddingBottom),
    left: Math.round(paddingLeft),
    right: Math.round(paddingRight),
  };

  return padding;
};

// Offset the map to the right when adding a new point
export const newPointOffset = (currentlatLon) => {
  const shiftedLatLon = shiftLatLonByPixelRelativeToMapContainer(
    mapReference,
    currentlatLon.lat,
    currentlatLon.lon,
    60,
    0,
  );
  return {
    lat: shiftedLatLon?.lat,
    lon: shiftedLatLon?.lng,
  };
};

export const fitMapToPlan = (fitBoundsPoints) => {
  const padding = calculateMapPadding();

  mapReference.fitBounds(fitBoundsPoints, {
    linear: true,
    // NOTE: works best in this use case if `offset` left blank and just supply padding
    padding,
    // maxZoom: 12,
  });
};

const loadTripPlanMarkerBase = async (longitude, latitude, marker) => {
  // If we have yet to receive a reference to the map object through the onInitialize function,
  // just return as we can't update the data on the layer if the layer doesn't exist yet.
  if (!mapReference) return;

  if (!latitude || !longitude) return;

  addLayersIfRequired();
  const markerIconName = await loadMarkerIconAsImage(marker);

  const { data } = formatAsGeoJSONPoint(longitude, latitude, {
    icon: markerIconName,
  });

  currentDisplayTripPlanMarkers.push({
    markerIconName,
    data,
  });

  updateLayerGeoJSONData();
};

export const loadTripPlanMarker = async (longitude, latitude, marker) => {
  if (mapReference) {
    await loadTripPlanMarkerBase(longitude, latitude, marker);
  } else {
    setTimeout(() => loadTripPlanMarker(longitude, latitude, marker), 500);
  }
};

export const removeTripPlanMarker = (marker) => {
  if (!mapReference) return;

  const iconName = marker.name;
  currentDisplayTripPlanMarkers = currentDisplayTripPlanMarkers.filter(
    (i) => i.markerIconName !== iconName,
  );
  updateLayerGeoJSONData();
};

export const removeAllTripPlanMarkers = () => {
  if (!mapReference) return;

  currentDisplayTripPlanMarkers = [];
  updateLayerGeoJSONData();
};

layer.onToggle = () => {};

layer.onInitialize = (map) => {
  mapReference = map;
  addLayersIfRequired();
};

export default layer;
